// Customer Dashboard — customer behaviour/relationship overview, wired to the
// Customer-specific backend aggregation queries via ONE combined GraphQL round
// trip (getCustomerDashboardDataAction), following the same pattern as
// DashboardScreen.tsx / SalesDashboardScreen.tsx / InventoryDashboardScreen.tsx.
//
// Dropped vs. the original brief (no backing data in the current schema):
// Outstanding Balance KPI/table (no due/credit-balance field on customerDetail
// or billing), Customer Segments donut (no segment/tier column — retail/
// wholesale/dealer/corporate), Customers by City map (no address/city field on
// customerDetail at all).
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { Users, UserPlus, Repeat, Wallet, ShoppingBag, Timer } from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getCustomerDashboardDataAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Skeleton } from "ui/skeleton";
import { ChartCard } from "./ChartCard";
import { TrendAreaChart } from "./TrendAreaChart";
import { CategoryDonutChart } from "./CategoryDonutChart";
import { CategoryBarChart } from "./CategoryBarChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);

const TABLE_TABS = [
  { key: "recentCustomers", label: "Recently Registered" },
  { key: "topSpending", label: "Top Spending Customers" },
  { key: "inactive", label: "Inactive Customers" },
] as const;

type TableTabKey = (typeof TABLE_TABS)[number]["key"];

const PAGE_SIZE = 10;

const getFinancialYearRange = () => {
  const today = new Date();
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    fromDate: moment({ year: startYear, month: 3, day: 1 }).format("YYYY-MM-DD"),
    toDate: moment({ year: startYear + 1, month: 2, day: 31 }).format("YYYY-MM-DD"),
    label: `Apr ${startYear} – Mar ${startYear + 1}`,
  };
};

const CustomerDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getCustomerDashboardData, getCustomerDashboardDataLoad } = apparelReducerState;

  const fy = React.useMemo(() => getFinancialYearRange(), []);
  const [activeTab, setActiveTab] = React.useState<TableTabKey>("recentCustomers");
  const [page, setPage] = React.useState(1);

  React.useEffect(() => { setPage(1); }, [activeTab]);

  React.useEffect(() => {
    dispatch(getCustomerDashboardDataAction({ fromDate: fy.fromDate, toDate: fy.toDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = getCustomerDashboardData?.getCustomerSummary;
  const loading = getCustomerDashboardDataLoad && !summary;

  const newCustomersTrend = (getCustomerDashboardData?.getCustomerGrowth || []).map((r: any) => ({ date: r.date, value: Number(r.newCustomers) || 0 }));
  const customerGrowth = React.useMemo(() => {
    let running = 0;
    return (getCustomerDashboardData?.getCustomerGrowth || []).map((r: any) => {
      running += Number(r.newCustomers) || 0;
      return { date: r.date, value: running };
    });
  }, [getCustomerDashboardData]);
  const repeatVsNew = (getCustomerDashboardData?.getRepeatVsNewCustomers || []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));
  const topCustomers = (getCustomerDashboardData?.getTopCustomers || []).map((r: any) => ({ label: r.customerName, value: Number(r.totalSpend) || 0 }));

  const recentCustomers = getCustomerDashboardData?.getRecentCustomers || [];
  const topSpending = getCustomerDashboardData?.getTopCustomers || [];
  const inactiveCustomers = getCustomerDashboardData?.getInactiveCustomers || [];

  const recentCustomersCsvRows = React.useMemo(
    () => recentCustomers.map((r: any) => ({ ...r, createdOn_fmt: r.createdOn ? moment(r.createdOn).format("DD-MMM-YYYY") : "-" })),
    [recentCustomers]
  );
  const topSpendingCsvRows = React.useMemo(
    () => topSpending.map((r: any) => ({ ...r, totalSpend: Math.round(Number(r.totalSpend) || 0) })),
    [topSpending]
  );
  const inactiveCsvRows = React.useMemo(
    () => inactiveCustomers.map((r: any) => ({ ...r, lastPurchaseDate_fmt: r.lastPurchaseDate ? moment(r.lastPurchaseDate).format("DD-MMM-YYYY") : "Never" })),
    [inactiveCustomers]
  );

  const recentCustomersColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => row.original?.customerName },
      { id: "mobileNumber", header: "Mobile", cell: ({ row }) => row.original?.customerDetailId ? (
        <button type="button" onClick={() => navigate("/customer", { state: { auditRecordId: row.original.customerDetailId, auditSearchCode: row.original.mobileNumber } })} className="text-primary hover:underline">
          {row.original.mobileNumber || row.original.customerName}
        </button>
      ) : "-" },
      { id: "createdOn", header: "Registered On", cell: ({ row }) => (row.original?.createdOn ? moment(row.original.createdOn).format("DD-MMM-YYYY") : "-") },
    ],
    [navigate]
  );

  const topSpendingColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => row.original?.customerName },
      { id: "mobileNumber", header: "Mobile", cell: ({ row }) => row.original?.customerDetailId ? (
        <button type="button" onClick={() => navigate("/customer", { state: { auditRecordId: row.original.customerDetailId, auditSearchCode: row.original.mobileNumber } })} className="text-primary hover:underline">
          {row.original.mobileNumber || row.original.customerName}
        </button>
      ) : "-" },
      { id: "billCount", header: "Bills", cell: ({ row }) => num(row.original?.billCount) },
      { id: "totalSpend", header: "Total Spend", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.totalSpend)}</span> },
    ],
    [navigate]
  );

  const inactiveColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => row.original?.customerName },
      { id: "mobileNumber", header: "Mobile", cell: ({ row }) => row.original?.customerDetailId ? (
        <button type="button" onClick={() => navigate("/customer", { state: { auditRecordId: row.original.customerDetailId, auditSearchCode: row.original.mobileNumber } })} className="text-primary hover:underline">
          {row.original.mobileNumber || row.original.customerName}
        </button>
      ) : "-" },
      {
        id: "lastPurchaseDate",
        header: "Last Purchase",
        cell: ({ row }) => (row.original?.lastPurchaseDate ? moment(row.original.lastPurchaseDate).format("DD-MMM-YYYY") : "Never"),
      },
    ],
    [navigate]
  );

  const allTabData: Record<TableTabKey, { columns: ColumnDef<any, any>[]; rows: any[] }> = {
    recentCustomers: { columns: recentCustomersColumns, rows: recentCustomers },
    topSpending: { columns: topSpendingColumns, rows: topSpending },
    inactive: { columns: inactiveColumns, rows: inactiveCustomers },
  };

  const activeTabData = allTabData[activeTab];
  const pagedData = activeTabData.rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.customerDashboard}
        </button>
      </div>

      <div className="mb-4">
        <p className="text-sm text-muted-foreground">Showing current financial year ({fy.label}).</p>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Total Customers" value={num(summary?.totalCustomers)} icon={Users} />
            <StatCard label="Active Customers" value={num(summary?.activeCustomers)} icon={Users} />
            <StatCard label="New Customers" value={num(summary?.newCustomers)} icon={UserPlus} />
            <StatCard label="Repeat Customers" value={num(summary?.repeatCustomers)} icon={Repeat} />
            <StatCard label="Customer Lifetime Value" value={inr(summary?.customerLifetimeValue)} icon={Wallet} />
            <StatCard label="Average Purchase Value" value={inr(summary?.averagePurchaseValue)} icon={ShoppingBag} />
            <StatCard label="Average Purchase Frequency" value={Number(summary?.averagePurchaseFrequency || 0).toFixed(1)} caption="bills / customer" icon={Timer} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="New Customers per Month" loading={getCustomerDashboardDataLoad && !getCustomerDashboardData} isEmpty={!newCustomersTrend.length}>
          <TrendAreaChart data={newCustomersTrend} valueFormatter={num} />
        </ChartCard>
        <ChartCard title="Customer Growth" loading={getCustomerDashboardDataLoad && !getCustomerDashboardData} isEmpty={!customerGrowth.length}>
          <TrendAreaChart data={customerGrowth} valueFormatter={num} />
        </ChartCard>
        <ChartCard title="Top Customers" loading={getCustomerDashboardDataLoad && !getCustomerDashboardData} isEmpty={!topCustomers.length}>
          <CategoryBarChart data={topCustomers} valueFormatter={inr} layout="vertical" />
        </ChartCard>
        <ChartCard title="Repeat vs New Customers" loading={getCustomerDashboardDataLoad && !getCustomerDashboardData} isEmpty={!repeatVsNew.length}>
          <CategoryDonutChart data={repeatVsNew} valueFormatter={num} />
        </ChartCard>
      </div>

      {/* Tables */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {TABLE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={
              activeTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      <DataTable
        columns={activeTabData.columns}
        data={pagedData}
        loading={getCustomerDashboardDataLoad && !getCustomerDashboardData}
        csvExport={
          activeTab === "recentCustomers"
            ? { fileName: `recently-registered_${moment().format("YYYY-MM-DD")}`, rows: recentCustomersCsvRows, columns: [{ key: "customerName", header: "Customer" }, { key: "mobileNumber", header: "Mobile" }, { key: "createdOn_fmt", header: "Registered On" }] }
            : activeTab === "topSpending"
            ? { fileName: `top-spending-customers_${moment().format("YYYY-MM-DD")}`, rows: topSpendingCsvRows, columns: [{ key: "customerName", header: "Customer" }, { key: "mobileNumber", header: "Mobile" }, { key: "billCount", header: "Bills" }, { key: "totalSpend", header: "Total Spend" }] }
            : { fileName: `inactive-customers_${moment().format("YYYY-MM-DD")}`, rows: inactiveCsvRows, columns: [{ key: "customerName", header: "Customer" }, { key: "mobileNumber", header: "Mobile" }, { key: "lastPurchaseDate_fmt", header: "Last Purchase" }] }
        }
        pagination={{
          page,
          total: activeTabData.rows.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
        }}
      />
    </div>
  );
};

export default CustomerDashboardScreen;
